import { alpha } from "@mui/material";
import { useMemo } from "react";

import { useContactCenterTheme } from "./ContactCenterThemeContext";

export function useContactCenterStyles() {
  const { theme } = useContactCenterTheme();

  return useMemo(() => {
    const accent = theme.shell.accent;
    const inset = theme.chat.inputBg;

    return {
      surface: {
        bgcolor: theme.shell.bg,
        color: theme.shell.textPrimary,
      },

      inset: {
        bgcolor: inset,
        color: theme.shell.textPrimary,
      },

      card: {
        bgcolor: theme.shell.headerBg,
        color: theme.shell.textPrimary,
        borderRadius: "12px",
        padding: 3,
        border: `1px solid ${theme.shell.border}`,
      },

      topCard: {
        bgcolor: theme.shell.headerBg,
        color: theme.shell.textPrimary,
        p: 2.5,
        borderRadius: "12px",
        display: "flex",
        flex: 1,
        justifyContent: "space-between",
        alignItems: "center",
        border: `1px solid ${theme.shell.border}`,
      },

      messageCard: {
        bgcolor: theme.shell.headerBg,
        color: theme.shell.textPrimary,
        p: 2.5,
        borderRadius: "12px",
        border: `1px solid ${theme.shell.border}`,
        transition: "transform 0.2s ease-in-out",
        "&:hover": { transform: "translateY(-2px)" },
      },

      label: {
        display: "block",
        fontWeight: 600,
        letterSpacing: "0.05em",
        color: theme.shell.textSecondary,
      },

      select: {
        borderRadius: "12px",
        bgcolor: inset,
        color: theme.shell.textPrimary,
        fontWeight: 400,
        fontSize: "0.9rem",
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiSvgIcon-root": { color: theme.shell.textSecondary },
      },

      slider: {
        color: accent,
        height: 6,
        "& .MuiSlider-track": { border: "none" },
        "& .MuiSlider-thumb": {
          width: 18,
          height: 18,
          backgroundColor: accent,
          border: `3px solid ${theme.shell.headerBg}`,
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          "&:hover, &.Mui-focusVisible, &.Mui-active": { boxShadow: "none" },
        },
        "& .MuiSlider-rail": { opacity: 0.35, bgcolor: theme.chat.scrollThumb },
      },

      miniSlider: {
        color: theme.chat.scrollThumb,
        height: 4,
        p: 0,
        "& .MuiSlider-thumb": { display: "none" },
        "& .MuiSlider-rail": { opacity: 1, bgcolor: inset },
        "& .MuiSlider-track": { bgcolor: accent, border: "none" },
      },

      timeLabel: {
        fontSize: "10px",
        fontWeight: 500,
        color: theme.shell.textSecondary,
      },

      phoneIconContainer: {
        width: 44,
        height: 44,
        bgcolor: alpha(accent, 0.12),
        color: accent,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      },

      playIconButton: {
        bgcolor: alpha(accent, 0.12),
        color: accent,
        width: 40,
        height: 40,
        "&:hover": { bgcolor: alpha(accent, 0.22) },
      },

      searchField: {
        "& .MuiOutlinedInput-root": {
          borderRadius: "16px",
          bgcolor: inset,
          color: theme.shell.textPrimary,
          "& fieldset": { border: "none" },
        },
        "& .MuiOutlinedInput-input": { py: 1.5, fontSize: "0.9rem" },
        "& input::placeholder": { color: theme.shell.textSecondary, opacity: 1 },
      },

      accentSwitch: {
        "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: accent },
        "& .MuiSwitch-track": { bgcolor: theme.chat.scrollThumb },
      },
    };
  }, [theme]);
}
